import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'

export interface CommonDeduction {
  reason: string
  points: number
  category: string
}

// Query keys factory
export const deductionsKeys = {
  all: ['deductions'] as const,
  common: () => [...deductionsKeys.all, 'common'] as const,
}

/**
 * Fetch common deductions
 */
export function useCommonDeductions() {
  return useQuery({
    queryKey: deductionsKeys.common(),
    queryFn: async () => {
      const data = await apiClient.get('/api/deductions/common')
      return (data || []) as CommonDeduction[]
    },
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Update all common deductions
 */
export function useUpdateCommonDeductions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (deductions: CommonDeduction[]) => {
      return await apiClient.post('/api/deductions/common/update', deductions)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deductionsKeys.common() })
    },
  })
}

/**
 * Add a new common deduction
 */
export function useAddCommonDeduction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (deduction: CommonDeduction) => {
      return await apiClient.post('/api/deductions/common/add', deduction)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deductionsKeys.common() })
    },
  })
}

/**
 * Delete a common deduction
 */
export function useDeleteCommonDeduction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (reason: string) => {
      return await apiClient.delete(`/api/deductions/common/delete?reason=${encodeURIComponent(reason)}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deductionsKeys.common() })
    },
  })
}

/**
 * Reset common deductions to defaults
 */
export function useResetCommonDeductions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      return await apiClient.post('/api/deductions/common/reset')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deductionsKeys.common() })
    },
  })
}
